import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { AssessmentQuestion } from '../../types'
import { Alert, Skeleton } from '../../components/ui'
import QuizForm from './QuizForm'

interface QuizResult { passed: boolean; score: number; pass_mark: number }

export default function FinalAssessment() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [result, setResult] = useState<QuizResult | null>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['learning', 'final-assessment', courseId],
    queryFn: () => api.get<{ ok: boolean; questions: AssessmentQuestion[]; pass_mark: number }>(
      `/learning/courses/${courseId}/assessment`,
    ),
    enabled: Boolean(courseId),
  })

  const submit = useMutation({
    mutationFn: (answers: number[]) => api.post<QuizResult>(
      `/learning/courses/${courseId}/assessment`, { answers },
    ),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['learning'] })
      setResult(res)
    },
  })

  if (isError) return <Alert tone="danger">{(error as Error).message}</Alert>
  if (isLoading || !data) return <div className="stack"><Skeleton h={30} w="40%" /><Skeleton h={140} /></div>

  return (
    <>
      <div className="page-head">
        <div><h1>Final assessment</h1></div>
      </div>
      {submit.isError && (
        <div style={{ marginBottom: 'var(--sp-4)' }}>
          <Alert tone="danger">{(submit.error as Error).message}</Alert>
        </div>
      )}
      <QuizForm
        questions={data.questions}
        passMark={data.pass_mark}
        submitting={submit.isPending}
        result={result}
        onSubmit={(answers) => submit.mutate(answers)}
        onContinue={() => {
          if (result?.passed) navigate(`/learning/${courseId}`)
          else setResult(null)
        }}
      />
    </>
  )
}
